import type { RequestedAdditionalGear } from '../models/AdditionalGear';

/**
 * Wall-clock helpers for booking the same room or lesson more than once.
 * Every start is sent on one create. The API books them as one series and
 * one payment. A new session defaults to the first session's clock time,
 * one week after the latest date.
 *
 * Matches `BookingOccurrenceStarts.MAX_SESSIONS` / `TutorBookingOccurrenceStarts.MAX_SESSIONS`.
 */

export const BOOKING_REPEAT_MAX_SESSIONS = 12;

export interface BookingSessionSpan {
  start: Date;
  durationMinutes: number;
}

export interface RoomBookingOccurrencePayload {
  startTime: string;
  durationInMinutes: number;
  /** Present when each session can rent a different set. Empty means none for that session. */
  additionalGears?: RequestedAdditionalGear[];
}

export interface TutorBookingOccurrencePayload {
  startTime: string;
}

/** Same clock time seven calendar days later. Calendar fields avoid a DST hour shift. */
export function bookingSlotPlusOneWeek(start: Date): Date {
  return new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate() + 7,
    start.getHours(),
    start.getMinutes(),
    start.getSeconds(),
    0,
  );
}

/** One week after `previous`, at the clock time of the first session. */
export function bookingSlotNextWeekAtFirstTime(first: Date, previous: Date): Date {
  const weekLater = bookingSlotPlusOneWeek(previous);
  return new Date(
    weekLater.getFullYear(),
    weekLater.getMonth(),
    weekLater.getDate(),
    first.getHours(),
    first.getMinutes(),
    first.getSeconds(),
    0,
  );
}

export function bookingSlotEnd(start: Date, durationMinutes: number): Date {
  return new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
    start.getHours(),
    start.getMinutes() + durationMinutes,
    start.getSeconds(),
    0,
  );
}

/** True when any two sessions share time. Each session keeps its own length. Back-to-back does not overlap. */
export function bookingSessionsOverlap(sessions: BookingSessionSpan[]): boolean {
  if (sessions.length < 2) return false;
  const order = sessions
    .map((session, index) => ({ session, index }))
    .sort((a, b) => a.session.start.getTime() - b.session.start.getTime());
  for (let n = 1; n < order.length; n += 1) {
    const previous = order[n - 1].session;
    const current = order[n].session;
    const previousEnd = bookingSlotEnd(previous.start, previous.durationMinutes);
    if (current.start.getTime() < previousEnd.getTime()) return true;
  }
  return false;
}

/**
 * Grow or shrink the extra sessions that follow `primary`.
 * Existing dates stay put. A newly added one is one week after the latest,
 * at the first session's clock time. `totalCount` includes the primary.
 */
export function resizeWeeklySessions(primary: Date, extras: Date[], totalCount: number): Date[] {
  const target = Math.max(1, Math.min(BOOKING_REPEAT_MAX_SESSIONS, Math.floor(totalCount)));
  const desired = target - 1;
  if (desired <= 0) return [];
  if (extras.length === desired) return extras;
  if (extras.length > desired) return extras.slice(0, desired);
  const next = [...extras];
  let cursor = next.length > 0 ? next[next.length - 1] : primary;
  while (next.length < desired) {
    const added = bookingSlotNextWeekAtFirstTime(primary, cursor);
    next.push(added);
    cursor = added;
  }
  return next;
}

export function appendBookingSession(starts: Date[]): Date[] {
  if (starts.length === 0 || starts.length >= BOOKING_REPEAT_MAX_SESSIONS) return starts;
  const first = starts[0];
  const last = starts[starts.length - 1];
  return [...starts, bookingSlotNextWeekAtFirstTime(first, last)];
}

/**
 * Omit the field for a single session. The first start must already equal the request `startTime`.
 * Pass `gearsBySession` so a series can rent different gear per date. Omit it to keep the
 * request-level gear list on every session.
 */
export function buildRoomBookingOccurrences(
  starts: Date[],
  durationMinutes: number,
  serializeStart: (start: Date) => string,
  gearsBySession?: RequestedAdditionalGear[][] | null,
): RoomBookingOccurrencePayload[] | undefined {
  if (starts.length <= 1) return undefined;
  return starts.map((start, index) => ({
    startTime: serializeStart(start),
    durationInMinutes: durationMinutes,
    ...(gearsBySession ? { additionalGears: gearsBySession[index] ?? [] } : {}),
  }));
}

/** Omit the field for a single lesson. The first start must already equal the request `startTime`. */
export function buildTutorBookingOccurrences(
  starts: Date[],
  serializeStart: (start: Date) => string,
): TutorBookingOccurrencePayload[] | undefined {
  if (starts.length <= 1) return undefined;
  return starts.map((start) => ({ startTime: serializeStart(start) }));
}
